import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ChevronDown, Clock } from 'lucide-react';
import { useDateTime } from '../../contexts/DateTimeContext';

// Custom TimeSelect component that respects user's time format preference
const TimeSelect = ({ value, onChange, className = '', placeholder, step = 30 }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const { timeFormat, dateLocale } = useDateTime();

  // Generate time options based on step (in minutes)
  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += step) {
      const hour = h.toString().padStart(2, '0');
      const minute = m.toString().padStart(2, '0');
      timeOptions.push(`${hour}:${minute}`);
    }
  }

  // Format time for display based on user preferences
  const formatTimeDisplay = (timeStr) => {
    if (!timeStr) return placeholder ?? t('common.select_time');
    const [hours, minutes] = timeStr.split(':').map(Number);

    if (timeFormat === '24h') {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    } else {
      // AM/PM in the app's language
      return format(new Date(2000, 0, 1, hours, minutes), 'h:mm a', { locale: dateLocale });
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll to selected time when dropdown opens
  useEffect(() => {
    if (isOpen && value && dropdownRef.current) {
      const selectedButton = dropdownRef.current.querySelector(`[data-time="${value}"]`);
      if (selectedButton) {
        selectedButton.scrollIntoView({ block: 'center', behavior: 'instant' });
      }
    }
  }, [isOpen, value]);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => {
          if (!isOpen && dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            setOpenUpward(window.innerHeight - rect.bottom < 260);
          }
          setIsOpen(prev => !prev);
        }}
        className="w-full px-3 py-2 bg-white/5 border border-gray-700 rounded-lg text-white text-left flex items-center justify-between hover:bg-white/10 transition-colors text-sm"
      >
        <span className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-400" />
          <span className={value ? 'text-white' : 'text-gray-500'}>{formatTimeDisplay(value)}</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className={`absolute z-50 w-full max-h-60 overflow-y-auto bg-[#252526] border border-gray-700 rounded-lg shadow-xl ${openUpward ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
          {timeOptions.map((time) => (
            <button
              key={time}
              type="button"
              data-time={time}
              onClick={() => {
                onChange(time);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                value === time ? 'bg-amber-500/20 text-amber-400' : 'text-gray-300'
              }`}
            >
              {formatTimeDisplay(time)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TimeSelect;
